// Shared Niño SST-anomaly globe helpers, used by both the embeddable graphic
// (src/Graphic.svelte) and the large screenshot page (src/Screenshot.svelte).
import mapboxgl from 'mapbox-gl';

const { Map } = mapboxgl;

export const MANIFEST_URL =
  'https://ix.cnn.io/data/el-nino-index/ssta-manifest.json';

// The Niño 3.4 monitoring region (lon/lat bounds).
export const REGION = { w: -170, e: -120, s: -5, n: 5 };

// A closed polygon tracing the Niño 3.4 box, stepped so it drapes nicely on the
// globe projection rather than cutting straight (chord) lines across the sphere.
export const ninoBox = (() => {
  const { w, e, s, n } = REGION;
  const step = 2;
  const pts = [];
  for (let lon = w; lon <= e; lon += step) pts.push([lon, s]);
  for (let lat = s; lat <= n; lat += step) pts.push([e, lat]);
  for (let lon = e; lon >= w; lon -= step) pts.push([lon, n]);
  for (let lat = n; lat >= s; lat -= step) pts.push([w, lat]);
  pts.push([w, s]);
  return { type: 'Polygon', coordinates: [pts.reverse()] };
})();

// Mapbox tile size in CSS px. worldSize = GLOBE_TILE_SIZE * 2^zoom is the
// equator's circumference on screen, so the globe's rendered *diameter* is
// worldSize / π.
const GLOBE_TILE_SIZE = 512;

/**
 * Zoom at which the globe's rendered diameter fills `pixelSize` CSS px.
 *
 * Derived from diameter = 512 * 2^zoom / π  ⇒  zoom = log2(D·π / 512).
 *
 * @param {number} pixelSize  target on-screen globe diameter (CSS px)
 * @param {number} fill       fraction of the container to fill (1 = edge-to-edge)
 * @returns {number}
 */
export const fitGlobeZoom = (pixelSize, fill = 1) =>
  Math.log2((pixelSize * fill * Math.PI) / GLOBE_TILE_SIZE);

/**
 * Create the Niño SST-anomaly globe.
 *
 * @param {string} tileset   mapbox://... raster tileset URL for the SST anomaly
 * @param {string|HTMLElement} container  map container id or element
 * @param {object} [options]
 * @param {number|'fit'} [options.zoom='fit']  globe zoom, or 'fit' to auto-size
 *        the hemisphere to the container (and keep it fitted on resize).
 * @param {number} [options.fill=1]  when fitting, fraction of the container the
 *        globe should fill (e.g. 0.95 to leave a small margin).
 * @param {boolean} [options.showLabels=false]  keep base-style place labels
 * @returns {import('mapbox-gl').Map}
 */
export const createNinoMap = (
  tileset,
  container,
  { zoom = 'fit', fill = 1, showLabels = false } = {}
) => {
  const el =
    typeof container === 'string'
      ? document.getElementById(container)
      : container;

  const autoFit = zoom === 'fit' || zoom == null;
  const getFitZoom = () =>
    fitGlobeZoom(Math.min(el.clientWidth, el.clientHeight), fill);

  const map = new Map({
    accessToken: import.meta.env.VITE_MAPBOX_TOKEN,
    container: el,
    style: 'mapbox://styles/cnndigital/cmtx3kkoo000101qm8j038xz4',
    projection: 'globe',
    zoom: autoFit ? getFitZoom() : zoom,
    // Allow small containers to still fit a full hemisphere (default minZoom 0).
    minZoom: -4,
    interactive: false,
    scrollZoom: false,
    attributionControl: false,
    center: [-125, 0]
  });

  // Re-fit the hemisphere whenever the container changes size.
  if (autoFit && typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => map.setZoom(getFitZoom()));
    ro.observe(el);
    map.on('remove', () => ro.disconnect());
  }

  map.on('load', () => {
    // Hide all label (symbol) layers from the base style unless requested.
    if (!showLabels) {
      map.getStyle().layers.forEach((layer) => {
        if (layer.type === 'symbol') {
          map.setLayoutProperty(layer.id, 'visibility', 'none');
        }
      });
    }

    map.addSource('ssta-raster', {
      type: 'raster',
      url: tileset
    });
    map.addLayer({
      id: 'ssta-raster',
      type: 'raster',
      source: 'ssta-raster'
    });

    map.addSource('nino-box', {
      type: 'geojson',
      data: ninoBox
    });
    map.addLayer({
      id: 'nino-box',
      source: 'nino-box',
      type: 'line',
      paint: {
        'line-color': '#333333',
        'line-width': 1
      }
    });
  });

  return map;
};

/**
 * Fetch the SST-anomaly manifest and return the pieces the pages need.
 * @returns {Promise<{manifestData: object, tileset: string, dates: number[]}>}
 */
export const fetchNinoManifest = async () => {
  const res = await fetch(MANIFEST_URL);
  const manifestData = await res.json();
  const tileset = `mapbox://${manifestData.mapbox['tileset_id']}`;
  const dates = manifestData.date_range.map((d) => Date.parse(d));
  return { manifestData, tileset, dates };
};
